import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaNewspaper, FaVideo, FaPlus, FaTrash, FaExternalLinkAlt, FaSave, FaUpload, FaPlayCircle, FaEdit } from 'react-icons/fa';

interface NewsMediaItem {
  id: number;
  title: string;
  category?: string;
  summary?: string;
  image?: string;
  source_link?: string;
  date?: string;
}

const NEWS_CATEGORIES = [
  'News Channel',
  'Print Media',
  'Radio Media',
  'Other News',
  'Media Coverage',
  'Press Release',
  'International Event'
];

const parseVideoEmbedUrl = (rawUrl?: string): { embedUrl: string; isDirectVideo: boolean; type: 'YOUTUBE' | 'INSTAGRAM' | 'FACEBOOK' | 'VIMEO' | 'DIRECT'; originalUrl: string } => {
  if (!rawUrl) return { embedUrl: '', isDirectVideo: false, type: 'YOUTUBE', originalUrl: '' };
  
  let url = rawUrl.trim();
  const lowerUrl = url.toLowerCase();

  // Direct MP4 / WebM / Uploaded Video
  if (
    lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg') || lowerUrl.endsWith('.mov') ||
    (lowerUrl.includes('uploads/') && !lowerUrl.includes('youtube') && !lowerUrl.includes('instagram') && !lowerUrl.includes('facebook'))
  ) {
    const fullUrl = url.startsWith('http') ? url : `https://rmbbackend.rmbjaipur.com/${url.replace(/\\/g, '/').replace(/^\//, '')}`;
    return { embedUrl: fullUrl, isDirectVideo: true, type: 'DIRECT', originalUrl: fullUrl };
  }

  // Instagram Reels or Posts
  const instaMatch = url.match(/(?:instagram\.com\/(?:reel|p|tv)\/([a-zA-Z0-9_-]+))/i);
  if (instaMatch && instaMatch[1]) {
    const reelId = instaMatch[1];
    return {
      embedUrl: `https://www.instagram.com/p/${reelId}/embed/`,
      isDirectVideo: false,
      type: 'INSTAGRAM',
      originalUrl: `https://www.instagram.com/reel/${reelId}/`
    };
  }

  // Facebook Reels or Videos
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.watch')) {
    const fbEmbedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
    return {
      embedUrl: fbEmbedUrl,
      isDirectVideo: false,
      type: 'FACEBOOK',
      originalUrl: url
    };
  }

  // YouTube Videos or Shorts
  const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/i);
  if (ytMatch && ytMatch[1]) {
    const videoId = ytMatch[1];
    return {
      embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1&enablejsapi=1`,
      isDirectVideo: false,
      type: 'YOUTUBE',
      originalUrl: `https://www.youtube.com/watch?v=${videoId}`
    };
  }

  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/i);
  if (vimeoMatch && vimeoMatch[1]) {
    return {
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      isDirectVideo: false,
      type: 'VIMEO',
      originalUrl: `https://vimeo.com/${vimeoMatch[1]}`
    };
  }

  return { embedUrl: url, isDirectVideo: false, type: 'YOUTUBE', originalUrl: url };
};

const NewsMediaAdmin: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'NEWS' | 'VIDEO'>('NEWS');
  const [newsList, setNewsList] = useState<NewsMediaItem[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);

  // New Media Form
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    category: 'News Channel',
    summary: '',
    image: '',
    source_link: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [isUploading, setIsUploading] = useState(false);

  // Edit Media Form State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<NewsMediaItem | null>(null);

  // Featured Video Form State
  const [videoList, setVideoList] = useState<any[]>([]);
  const [editingVideoId, setEditingVideoId] = useState<number | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoForm, setVideoForm] = useState({
    title: '',
    video_url: '',
    description: ''
  });
  const [savingVideo, setSavingVideo] = useState(false);

  useEffect(() => {
    fetchNewsMedia();
    fetchFeaturedVideos();
  }, []);

  const fetchNewsMedia = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/news-media');
      setNewsList(res.data.data || []);
    } catch (err) {
      console.error('Error fetching news media:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedVideos = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/featured-videos');
      const raw = res.data?.data || res.data || [];
      setVideoList(Array.isArray(raw) ? raw : [raw]);
    } catch (err) {
      console.error('Error fetching featured videos:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploading(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewItem(prev => ({ ...prev, image: res.data.imagePath }));
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateNews = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.title.trim()) return alert('Please enter article title');

    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/news-media', newItem);
      alert('📰 RMB News & Media article added successfully!');
      setShowAddModal(false);
      setNewItem({
        title: '',
        category: 'Media Coverage',
        summary: '',
        image: '',
        source_link: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchNewsMedia();
    } catch (err) {
      console.error('Error creating news item:', err);
      alert('Failed to add news media article');
    }
  };

  const handleEditFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploading(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingItem(prev => (prev ? { ...prev, image: res.data.imagePath } : null));
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenEditModal = (item: NewsMediaItem) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const handleUpdateNews = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.title.trim()) return alert('Please enter article title');

    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/news-media/${editingItem.id}`, editingItem);
      alert('📰 RMB News & Media article updated successfully!');
      setShowEditModal(false);
      setEditingItem(null);
      fetchNewsMedia();
    } catch (err) {
      console.error('Error updating news item:', err);
      alert('Failed to update news media article');
    }
  };

  const handleDeleteNews = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/news-media/${id}`);
      alert('Article deleted');
      fetchNewsMedia();
    } catch (err) {
      console.error('Error deleting news article:', err);
      alert('Failed to delete article');
    }
  };

  const handleSaveVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoForm.video_url.trim()) {
      alert('Video / Reel URL is required!');
      return;
    }
    setSavingVideo(true);
    try {
      if (editingVideoId) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/featured-video/${editingVideoId}`, videoForm);
        alert('🎬 Featured Video / Reel updated successfully!');
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/featured-video', videoForm);
        alert('🎬 New Featured Video / Reel added successfully!');
      }
      setShowVideoModal(false);
      setEditingVideoId(null);
      setVideoForm({ title: '', video_url: '', description: '' });
      fetchFeaturedVideos();
    } catch (err) {
      console.error('Error saving video:', err);
      alert('Failed to save featured video');
    } finally {
      setSavingVideo(false);
    }
  };

  const handleDeleteVideo = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this featured video / reel?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/featured-video/${id}`);
      alert('Featured video / reel deleted.');
      fetchFeaturedVideos();
    } catch (err) {
      console.error('Error deleting video:', err);
      alert('Failed to delete video.');
    }
  };

  return (
    <div className="p-6 bg-slate-900 min-h-screen text-slate-100 space-y-6">
      {/* Header */}
      <div className="bg-[#1E2B45] p-5 rounded-2xl border border-blue-500/40 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-xl">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            <FaNewspaper className="text-[#FDBC12]" /> RMB News & Media & Featured Video Management
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage press coverage, media highlights, news articles, and chapter featured video displayed on the home page.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex bg-[#131E32] p-1.5 rounded-xl border border-[#2A3B5C]">
          <button
            onClick={() => setActiveSubTab('NEWS')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeSubTab === 'NEWS' ? 'bg-[#FDBC12] text-[#0A2451] font-black' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaNewspaper /> News & Media Articles ({newsList.length})
          </button>
          <button
            onClick={() => setActiveSubTab('VIDEO')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeSubTab === 'VIDEO' ? 'bg-[#FDBC12] text-[#0A2451] font-black' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaVideo /> Featured Chapter Video
          </button>
        </div>
      </div>

      {/* SUB-TAB 1: NEWS & MEDIA ARTICLES */}
      {activeSubTab === 'NEWS' && (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-between items-center bg-[#1E2B45] p-4 rounded-xl border border-[#2A3B5C] gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-extrabold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                📰 Active News &amp; Media Articles
              </h2>
              <span className="text-xs bg-[#0A2451] text-amber-400 font-bold px-2.5 py-0.5 rounded-full border border-amber-500/30">
                {newsList.filter(n => filterCategory === 'ALL' || (n.category || '').toLowerCase() === filterCategory.toLowerCase()).length} Articles
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Category Segregation Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300 font-bold">Category Segregation:</span>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-1.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold text-xs outline-none cursor-pointer"
                >
                  <option value="ALL">All Categories ({newsList.length})</option>
                  {NEWS_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {cat} ({newsList.filter(n => (n.category || '').toLowerCase() === cat.toLowerCase()).length})
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-lg shadow flex items-center gap-1.5 cursor-pointer"
              >
                <FaPlus /> Add Media Article
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {newsList
              .filter(item => filterCategory === 'ALL' || (item.category || '').toLowerCase() === filterCategory.toLowerCase())
              .map(item => (
              <div key={item.id} className="bg-[#1E2B45] rounded-xl border border-[#2A3B5C] overflow-hidden flex flex-col justify-between shadow-lg">
                {item.image ? (
                  <img
                    src={item.image.startsWith('http') ? item.image : `https://rmbbackend.rmbjaipur.com/${item.image.replace(/^\//, '')}`}
                    alt={item.title}
                    className="h-44 w-full object-cover"
                  />
                ) : (
                  <div className="h-44 w-full bg-slate-800 flex items-center justify-center text-slate-500 font-bold text-xs">
                    No Media Image Attached
                  </div>
                )}

                <div className="p-4 space-y-2 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center text-[11px] font-bold text-[#FDBC12] mb-1">
                      <span>{item.category || 'Media Coverage'}</span>
                      <span className="text-slate-400">{item.date}</span>
                    </div>
                    <h3 className="font-bold text-white text-sm leading-snug line-clamp-2">{item.title}</h3>
                    <p className="text-slate-300 text-xs mt-1 leading-relaxed line-clamp-3">{item.summary}</p>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-[#2A3B5C] mt-2">
                    {item.source_link ? (
                      <a
                        href={item.source_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-xs font-bold flex items-center gap-1"
                      >
                        Read Link <FaExternalLinkAlt size={10} />
                      </a>
                    ) : <span />}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenEditModal(item)}
                        className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 font-bold text-xs rounded-lg border border-amber-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaEdit size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNews(item.id)}
                        className="px-3 py-1 bg-red-600/30 hover:bg-red-600 text-red-300 hover:text-white font-bold text-xs rounded-lg border border-red-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaTrash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: FEATURED CHAPTER VIDEOS & REELS CONFIGURATION */}
      {activeSubTab === 'VIDEO' && (
        <div className="space-y-6">
          <div className="bg-[#1E2B45] p-5 rounded-2xl border border-blue-500/40 shadow-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-base font-black text-amber-300 uppercase tracking-wider flex items-center gap-2">
                🎬 Homepage Featured Videos &amp; Reels ({videoList.length})
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Manage YouTube videos, Instagram Reels, Facebook Reels, and video files displayed on the home page.
              </p>
            </div>
            <button
              onClick={() => {
                setEditingVideoId(null);
                setVideoForm({ title: '', video_url: '', description: '' });
                setShowVideoModal(true);
              }}
              className="px-4 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg transition cursor-pointer flex items-center gap-2"
            >
              <FaPlus /> Add New Featured Video / Reel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videoList.length === 0 ? (
              <div className="col-span-full bg-[#1E2B45] p-12 rounded-2xl border border-[#2A3B5C] text-center text-slate-400">
                No featured videos or reels added yet.
              </div>
            ) : (
              videoList.map((vid) => {
                const info = parseVideoEmbedUrl(vid.video_url);
                return (
                  <div key={vid.id} className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] overflow-hidden shadow-xl flex flex-col justify-between hover:border-[#FDBC12] transition">
                    <div>
                      <div className="relative aspect-video w-full overflow-hidden bg-slate-950 border-b border-[#2A3B5C]">
                        {info.isDirectVideo ? (
                          <video src={info.embedUrl} controls className="w-full h-full object-cover" />
                        ) : (
                          <iframe
                            src={info.embedUrl}
                            title={vid.title}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        )}
                        <span className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm px-2.5 py-1 rounded-full text-[10px] font-black uppercase text-amber-400 border border-amber-400/30">
                          {info.type === 'INSTAGRAM' ? '📸 Instagram Reel' :
                           info.type === 'FACEBOOK' ? '📘 Facebook Reel' :
                           info.type === 'VIMEO' ? '🎬 Vimeo' :
                           info.type === 'DIRECT' ? '📹 Video' : '🔴 YouTube'}
                        </span>
                      </div>

                      <div className="p-4 space-y-2">
                        <h3 className="font-bold text-white text-sm line-clamp-2">{vid.title}</h3>
                        {vid.description && <p className="text-xs text-slate-400 line-clamp-2">{vid.description}</p>}
                      </div>
                    </div>

                    <div className="p-4 pt-0 flex items-center justify-between gap-2 border-t border-[#2A3B5C]/50 mt-2">
                      <button
                        onClick={() => {
                          setEditingVideoId(vid.id);
                          setVideoForm({ title: vid.title, video_url: vid.video_url, description: vid.description || '' });
                          setShowVideoModal(true);
                        }}
                        className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 font-bold text-xs rounded-lg border border-amber-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaEdit size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteVideo(vid.id)}
                        className="px-3 py-1.5 bg-red-600/30 hover:bg-red-600 text-red-300 hover:text-white font-bold text-xs rounded-lg border border-red-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaTrash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* FEATURED VIDEO MODAL */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-lg p-6 relative text-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-black text-white border-b border-[#2A3B5C] pb-3 mb-4">
              {editingVideoId ? '✏️ Edit Featured Video / Reel' : '🎬 Add New Featured Video / Reel'}
            </h3>

            <form onSubmit={handleSaveVideo} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Video / Reel Title *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                  placeholder="e.g. Discover Rotary Means Business Jaipur"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Video / Reel URL * (YouTube, Instagram, Facebook, MP4)</label>
                <input
                  type="text"
                  value={videoForm.video_url}
                  onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })}
                  placeholder="e.g. https://www.instagram.com/reel/... or https://youtube.com/watch?v=..."
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-mono text-xs outline-none"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">Supports YouTube (`watch?v=...`), Instagram Reels (`instagram.com/reel/...`), Facebook Reels, and MP4 links.</p>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                  rows={3}
                  placeholder="Brief summary of the video content..."
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
                />
              </div>

              {/* Video Live Preview */}
              {videoForm.video_url && (
                <div className="bg-[#131E32] p-3 rounded-xl border border-[#2A3B5C] space-y-2">
                  <span className="font-bold text-emerald-400 text-xs flex items-center gap-1.5">
                    <FaPlayCircle /> Live Stream Preview:
                  </span>
                  <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-700 bg-slate-950">
                    {(() => {
                      const info = parseVideoEmbedUrl(videoForm.video_url);
                      if (info.isDirectVideo) {
                        return <video src={info.embedUrl} controls className="w-full h-full object-cover" />;
                      }
                      return (
                        <iframe
                          src={info.embedUrl}
                          title="Video Preview"
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-3 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowVideoModal(false)}
                  className="px-4 py-2 bg-[#131E32] hover:bg-slate-800 text-slate-300 font-bold rounded-lg border border-[#2A3B5C]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingVideo}
                  className="px-5 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-lg shadow cursor-pointer"
                >
                  {savingVideo ? 'Saving...' : (editingVideoId ? 'Update Video' : 'Add Video')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW ARTICLE MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-lg p-6 relative text-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-black text-white border-b border-[#2A3B5C] pb-3 mb-4">
              ➕ Add New RMB News & Media Article
            </h3>

            <form onSubmit={handleCreateNews} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Title *</label>
                <input
                  type="text"
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="e.g. RMB Jaipur Expands Business Referral Network"
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Category *</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none cursor-pointer"
                  >
                    {NEWS_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Publication Date</label>
                  <input
                    type="text"
                    value={newItem.date}
                    onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                    placeholder="e.g. July 25, 2026"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Summary</label>
                <textarea
                  value={newItem.summary}
                  onChange={(e) => setNewItem({ ...newItem, summary: e.target.value })}
                  rows={3}
                  placeholder="Brief summary of the media coverage or press release..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">External News Link (Optional)</label>
                <input
                  type="url"
                  value={newItem.source_link}
                  onChange={(e) => setNewItem({ ...newItem, source_link: e.target.value })}
                  placeholder="https://..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              {/* Upload Image */}
              <div>
                <label className="font-bold text-slate-300 block mb-1">Cover Image / Media Clipping</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newItem.image}
                    onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                    placeholder="Image URL or upload..."
                    className="flex-1 p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
                  />
                  <label className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg cursor-pointer flex items-center gap-1.5">
                    <FaUpload /> {isUploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow cursor-pointer"
                >
                  Save Media Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT ARTICLE MODAL */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-lg p-6 relative text-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-black text-amber-300 border-b border-[#2A3B5C] pb-3 mb-4 flex items-center gap-2">
              ✏️ Edit RMB News & Media Article (ID: #{editingItem.id})
            </h3>

            <form onSubmit={handleUpdateNews} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Title *</label>
                <input
                  type="text"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  placeholder="Article Title..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Category *</label>
                  <select
                    value={editingItem.category || 'News Channel'}
                    onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none cursor-pointer"
                  >
                    {NEWS_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Publication Date</label>
                  <input
                    type="text"
                    value={editingItem.date || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, date: e.target.value })}
                    placeholder="e.g. July 25, 2026"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Summary / Content</label>
                <textarea
                  value={editingItem.summary || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, summary: e.target.value })}
                  rows={4}
                  placeholder="Summary or complete article text..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">External Media Link (Optional)</label>
                <input
                  type="url"
                  value={editingItem.source_link || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, source_link: e.target.value })}
                  placeholder="https://..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              {/* Upload Image */}
              <div>
                <label className="font-bold text-slate-300 block mb-1">Cover Image / Media Clipping</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingItem.image || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })}
                    placeholder="Image URL or upload..."
                    className="flex-1 p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
                  />
                  <label className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg cursor-pointer flex items-center gap-1.5">
                    <FaUpload /> {isUploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleEditFileUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#0A2451] font-black rounded-xl shadow cursor-pointer transition"
                >
                  Update Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsMediaAdmin;
